import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

function Section({ title, children, testId }) {
  return (
    <section className="rounded-xl bg-[#14452F]/60 border border-[#D4AF37]/15 p-4" data-testid={testId}>
      <h2 className="font-serif text-lg text-[#D4AF37] mb-1.5">{title}</h2>
      <div className="font-sans text-sm text-[#E0E0D1] leading-relaxed space-y-1.5">
        {children}
      </div>
    </section>
  );
}

export default function HowToPlay() {
  const navigate = useNavigate();

  // Go back to where the user came from, but fall back to Home on a deep-link
  // (bookmark / shared link / PWA) so the back button never leaves the app.
  const goBack = () => {
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="min-h-[100dvh] w-full bg-[#0d2a1c] flex justify-center" data-testid="how-to-play-screen">
      <div className="w-full max-w-md px-5 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={goBack}
            data-testid="how-to-play-back-btn"
            aria-label="Torna indietro"
            className="w-10 h-10 rounded-full bg-[#2C1A14] border border-[#D4AF37]/30 flex items-center justify-center text-[#FDFBF7] transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50"
          >
            <ArrowLeft className="w-5 h-5" aria-hidden="true" />
          </button>
          <h1 className="font-serif text-3xl font-bold text-[#FDFBF7]">Come si gioca a Scopa</h1>
        </div>

        <div className="flex flex-col gap-3">
          <Section title="Obiettivo" testId="section-obiettivo">
            <p>
              Si gioca contro il computer con un mazzo napoletano di 40 carte.
              Vince chi arriva per primo a 11 punti. Se al raggiungimento di 11
              punti c’è parità, si gioca un’altra mano.
            </p>
          </Section>

          <Section title="Come si svolge una mano" testId="section-mano">
            <p>
              A inizio mano ci sono quattro carte sul tavolo e ognuno riceve tre
              carte in mano. Si gioca a turno, una carta alla volta. Quando le
              carte in mano finiscono, se ne ricevono altre tre finché il mazzo
              non si esaurisce. Le carte rimaste sul tavolo alla fine della mano
              vanno all’ultimo che ha effettuato una presa.
            </p>
          </Section>

          <Section title="Come si prendono le carte" testId="section-prese">
            <p>Quando giochi una carta puoi prendere:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>una carta del tavolo di valore uguale alla tua;</li>
              <li>
                oppure più carte del tavolo la cui somma è uguale al valore
                della tua carta.
              </li>
            </ul>
            <p>
              Se sul tavolo c’è una carta dello stesso valore, sei obbligato a
              prendere quella singola. Se non puoi prendere nulla, la carta
              resta sul tavolo.
            </p>
          </Section>

          <Section title="La Scopa" testId="section-scopa">
            <p>
              Fai Scopa quando con una presa svuoti completamente il tavolo.
              Ogni Scopa vale un punto. Non conta la Scopa fatta con l’ultima
              carta dell’ultima mano.
            </p>
          </Section>

          <Section title="I punti di fine mano" testId="section-punti">
            <p>Alla fine di ogni mano si assegnano i punti così:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li><strong>Carte:</strong> un punto a chi ha preso più carte.</li>
              <li><strong>Denari:</strong> un punto a chi ha preso più carte del seme denari.</li>
              <li><strong>Settebello:</strong> un punto a chi ha preso il sette di denari.</li>
              <li>
                <strong>Primiera:</strong> un punto a chi ha la primiera più alta.
                Conta la carta migliore per ciascun seme, dove i sette valgono di
                più, poi i sei e gli assi.
              </li>
              <li><strong>Scope:</strong> un punto per ogni Scopa realizzata.</li>
            </ul>
            <p>In caso di parità su carte, denari o primiera, quel punto non viene assegnato a nessuno.</p>
          </Section>

          <Section title="Quando finisce la partita" testId="section-fine">
            <p>
              I punti di ogni mano si sommano. La partita finisce quando qualcuno
              raggiunge almeno 11 punti ed è in vantaggio. Chi ha più punti vince.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
